import { ActionIcon, Box, Button, Flex, Loader, Modal, Text } from '@mantine/core';
import { ArrowLeftIcon } from '@phosphor-icons/react';
import dayjs from 'dayjs';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { FC } from 'react';
import type { Area } from 'react-easy-crop';

import { USER_UPLOADS_FOLDER_ID } from '#constants/database-ids.ts';

import { env } from '../../../env.ts';
import { getAuthHeaders } from '../../../services/directusClient/directusClient.ts';
import { getCroppedImage } from './getCroppedImage.ts';
import { ImageCropper } from './ImageCropper.tsx';
import { ImagePreview } from './ImagePreview.tsx';
import { ImageUploadProgress } from './ImageUploadProgress.tsx';
import { uploadFileWithProgress } from './uploadFileWithProgress.ts';

type ModalStep = 'crop' | 'uploading' | 'preview';

export interface CropData {
  x: number;
  y: number;
  zoom: number;
}

interface ImageEditorModalProps {
  opened: boolean;
  imageSrc: string | null;
  /** 'edit-crop' = re-crop existing (upload cropped only), 'new' = new image (upload both) */
  mode: 'edit-crop' | 'new';
  /** Initial crop position/zoom to restore from previous session */
  initialCropData?: CropData | null;
  onClose: () => void;
  onComplete: (croppedFileId: string, originalFileId: string | null, previewSrc?: string, cropData?: CropData) => void;
  originalFile: File | null;
  /** Existing original file ID for edit-crop mode (so we don't re-upload it) */
  existingOriginalId?: string | null;
}

export const ImageEditorModal: FC<ImageEditorModalProps> = ({
  opened,
  imageSrc,
  mode,
  initialCropData,
  onClose,
  onComplete,
  originalFile,
  existingOriginalId,
}) => {
  const [step, setStep] = useState<ModalStep>('crop');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [croppedPreviewSrc, setCroppedPreviewSrc] = useState<string | null>(null);
  const croppedAreaPixelsRef = useRef<Area | null>(null);
  const currentCropDataRef = useRef<CropData | null>(initialCropData ?? null);

  // Reset when modal opens or image changes
  useEffect(() => {
    if (opened && imageSrc) {
      setStep('crop');
      setUploadProgress(0);
      setUploadError(null);
      setCroppedPreviewSrc(null);
      croppedAreaPixelsRef.current = null;
    }
  }, [opened, imageSrc]);

  const handleCropComplete = useCallback((croppedAreaPixels: Area) => {
    croppedAreaPixelsRef.current = croppedAreaPixels;
  }, []);

  const handleCropChange = useCallback((crop: { x: number; y: number }, zoom: number) => {
    currentCropDataRef.current = { x: crop.x, y: crop.y, zoom };
  }, []);

  const handleNext = useCallback(async () => {
    if (!imageSrc || !croppedAreaPixelsRef.current) return;
    // In 'new' mode we need the original file to upload
    if (mode === 'new' && !originalFile) return;

    const croppedBlob = await getCroppedImage(imageSrc, croppedAreaPixelsRef.current);
    if (!croppedBlob) return;

    const previewUrl = URL.createObjectURL(croppedBlob);
    setCroppedPreviewSrc(previewUrl);
    setStep('uploading');
    setUploadError(null);
    setUploadProgress(0);

    try {
      const today = dayjs().format('YYYY-MM-DD HH:mm:ss.SSS');
      const headers = await getAuthHeaders();
      let originalId: string | null = existingOriginalId ?? null;

      if (mode === 'new' && originalFile) {
        // Upload original file (70% of progress)
        const originalResult = await uploadFileWithProgress({
          file: originalFile,
          fileName: `event-image-original-[${today}].${originalFile.name.split('.').pop() ?? 'png'}`,
          title: 'Original image of event',
          folder: USER_UPLOADS_FOLDER_ID,
          apiUrl: env.VITE_DIRECTUS_API_URL,
          headers,
          onProgress: (p) => setUploadProgress(Math.round(p * 0.7)),
        });
        originalId = originalResult.id;
      }

      // Upload cropped file
      const croppedProgressOffset = mode === 'new' ? 70 : 0;
      const croppedProgressScale = mode === 'new' ? 0.3 : 1;
      const croppedResult = await uploadFileWithProgress({
        file: croppedBlob,
        fileName: `event-image-[${today}].png`,
        title: 'Image of event',
        folder: USER_UPLOADS_FOLDER_ID,
        apiUrl: env.VITE_DIRECTUS_API_URL,
        headers,
        onProgress: (p) => setUploadProgress(croppedProgressOffset + Math.round(p * croppedProgressScale)),
      });

      setUploadProgress(100);
      setStep('preview');
      onComplete(croppedResult.id, originalId, previewUrl, currentCropDataRef.current ?? undefined);
    } catch {
      setUploadError('Upload failed. Please try again');
    }
  }, [imageSrc, originalFile, onComplete, mode, existingOriginalId]);

  // handleNext catches its own failures and shows them as uploadError, so its promise is not awaited.
  const handleRetry = useCallback(() => {
    void handleNext();
  }, [handleNext]);

  const handleRecrop = useCallback(() => {
    setStep('crop');
    setUploadError(null);
    setUploadProgress(0);
  }, []);

  const handleCancel = useCallback(() => {
    setStep('crop');
    setUploadError(null);
    setUploadProgress(0);
    setCroppedPreviewSrc(null);
    onClose();
  }, [onClose]);

  const handleDone = useCallback(() => {
    onClose();
  }, [onClose]);

  const isLoading = !imageSrc;

  return (
    <Modal
      fullScreen
      opened={opened}
      styles={{
        content: { backgroundColor: '#0a0a0a' },
        header: { backgroundColor: '#0a0a0a' },
        body: { height: 'calc(100vh - 60px)', display: 'flex', flexDirection: 'column', padding: 0 },
      }}
      title={null}
      withCloseButton={false}
      onClose={handleCancel}
    >
      {/* Screen reader announcements */}
      <div aria-live="polite" style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden' }}>
        {step === 'uploading' && `Uploading your artwork, ${uploadProgress} percent complete`}
        {step === 'preview' && 'Upload complete. Preview your artwork.'}
        {uploadError}
      </div>

      {/* Header */}
      <Flex
        align="center"
        justify="space-between"
        px="md"
        py="sm"
        style={{ borderBottom: '1px solid rgba(255,255,255,0.1)' }}
      >
        <ActionIcon
          aria-label={step === 'preview' ? 'Go back to crop' : 'Cancel'}
          c="dimmed"
          size="lg"
          variant="subtle"
          onClick={step === 'preview' ? handleRecrop : handleCancel}
        >
          <ArrowLeftIcon size={20} />
        </ActionIcon>
        <Text c="white" fw={600} size="sm">
          {step === 'crop' && 'Adjust your artwork'}
          {step === 'uploading' && 'Uploading...'}
          {step === 'preview' && 'Preview'}
        </Text>
        {step === 'crop' ? (
          <Button color="blue.5" radius="md" size="xs" onClick={() => void handleNext()}>
            Next
          </Button>
        ) : step === 'preview' ? (
          <Button color="blue.5" radius="md" size="xs" onClick={handleDone}>
            Done
          </Button>
        ) : (
          <Box w={60} />
        )}
      </Flex>

      {/* Body */}
      <Box style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        {isLoading && (
          <Flex align="center" h="100%" justify="center" style={{ flex: 1 }}>
            <Loader color="gray" />
          </Flex>
        )}

        {!isLoading && step === 'crop' && (
          <ImageCropper
            imageSrc={imageSrc}
            initialCrop={initialCropData ? { x: initialCropData.x, y: initialCropData.y } : null}
            initialZoom={initialCropData?.zoom ?? null}
            onCropChange={handleCropChange}
            onCropComplete={handleCropComplete}
          />
        )}

        {step === 'uploading' && croppedPreviewSrc && (
          <ImageUploadProgress
            error={uploadError}
            previewSrc={croppedPreviewSrc}
            progress={uploadProgress}
            onRetry={handleRetry}
          />
        )}

        {step === 'preview' && croppedPreviewSrc && (
          <ImagePreview previewSrc={croppedPreviewSrc} onRecrop={handleRecrop} />
        )}
      </Box>
    </Modal>
  );
};
